import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import { ApiError } from "./types/common-types";
import {
  TopupPlanState,
  TopupcardPlanResponse,
  TopupcardPlan,
  FilterOptions,
} from "./types/topup-types";
import { PaginationReturn } from "./types/types";

const initialState: TopupPlanState = {
  topupcardPlans: [],
  topupcardPlan: null,
  selectedTopupcardPlan: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  filters: {},
  filterOptions: null,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// ✅ Create TopupPlan
export const createTopupcardPlan = createAsyncThunk<
  TopupcardPlanResponse,
  any,
  { rejectValue: ApiError; state: RootState }
>("topupcardPlans/create", async (dataPlanData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/top-ups`, dataPlanData, {
      headers: {
        Authorization: `Bearer ${token}`,
        // 'Content-Type': 'application/json' do not dd this line, allow browser to handle
      },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data plan" }
    );
  }
});

// ✅ Fetch TopupPlans (Paginated)
export const fetchTopupcardPlans = createAsyncThunk<
  TopupcardPlanResponse & PaginationReturn,
  any | { url: string },
  { rejectValue: ApiError; state: RootState }
>("topupcardPlans/fetch", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/top-ups`;
    const res = await axios.get(apiUrl, {
      params: urlFilters,
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.message,
      data: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch topup plans" }
    );
  }
});

// ✅ Fetch Single TopupPlan by ID
export const fetchTopupcardPlanById = createAsyncThunk<
  TopupcardPlan,
  number,
  { rejectValue: ApiError; state: RootState }
>("topupcardPlans/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/top-ups/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch topup plan" }
    );
  }
});

// ✅ Delete TopupPlan
export const deleteTopupcardPlan = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("topupcardPlans/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/top-ups/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete top up plan" }
    );
  }
});

// ✅ Update TopupPlan
export const updateTopupcardPlan = createAsyncThunk<
  TopupcardPlan,
  { id: number; data: Partial<TopupcardPlan> | FormData },
  { rejectValue: ApiError; state: RootState }
>("topupcardPlans/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.post(`${API_URL}/top-ups/${id}?_method=PUT`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update top up plan" }
    );
  }
});

// ✅ Fetch Filter Options
export const fetchFilterOptions = createAsyncThunk<
  FilterOptions,
  void,
  { rejectValue: ApiError; state: RootState }
>("topupcardPlans/fetchFilterOptions", async (_, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/top-ups/filter-options`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch filter options" }
    );
  }
});

// ✅ Slice
const topupcardPlanSlice = createSlice({
  name: "topupcardPlans",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.currentPage = 1; // Reset to first page when filters change
    },
    clearFilters: (state) => {
      state.filters = {};
      state.currentPage = 1;
    },
    resetTopupcardPlanState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedTopupcardPlan = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createTopupcardPlan.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createTopupcardPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.topupcardPlan) {
          state.topupcardPlans.unshift(action.payload.topupcardPlan);
        }
      })
      .addCase(createTopupcardPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to create data plan";
      })

      // Fetch All
      .addCase(fetchTopupcardPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTopupcardPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.topupcardPlans = action.payload.data || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchTopupcardPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch data plans";
      })
      .addCase(fetchFilterOptions.fulfilled, (state, action) => {
        state.filterOptions = action.payload;
      })

      // Fetch One
      .addCase(fetchTopupcardPlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedTopupcardPlan = null;
      })
      .addCase(fetchTopupcardPlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedTopupcardPlan = action.payload;
      })
      .addCase(fetchTopupcardPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch data plan";
      })

      // Delete
      .addCase(deleteTopupcardPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteTopupcardPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.topupcardPlans = state.topupcardPlans.filter(
          (plan) => plan.id !== action.payload
        );
      })
      .addCase(deleteTopupcardPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to delete data plan";
      })

      // Update
      .addCase(updateTopupcardPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateTopupcardPlan.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;

        // Update data plan in list
        state.topupcardPlans = state.topupcardPlans.map((plan) =>
          plan.id === action.payload.id ? action.payload : plan
        );

        // Update selected data plan if it's the one updated
        if (state.selectedTopupcardPlan?.id === action.payload.id) {
          state.selectedTopupcardPlan = action.payload;
        }
      })
      .addCase(updateTopupcardPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to update data plan";
      });
  },
});

export const { setFilters, clearFilters, resetTopupcardPlanState } =
  topupcardPlanSlice.actions;
export default topupcardPlanSlice.reducer;
